<script setup lang="ts">
import { computed, ref } from 'vue'
import { VModal } from '@/modules/shared/components'
import { useModal } from '@/modules/shared/composables/use-modal'
import { useRoute } from 'vue-router'
import VLoadingSection from '@/modules/shared/components/v-loading-section.vue'
import { format } from '@/modules/shared/utils/v-table'
import { usePortfolioDistributionStore } from '@/modules/investing/stores/portfolio-distribution-store'
import { DisbursementAllocation } from '@/modules/investing/types'
import { useExtendedI18n } from '@/i18n'
import DescriptionList from '@/modules/shared/components/DescriptionList.vue'
import { rails_url } from '@/modules/shared/utils/rails'

const props = defineProps<{
  portfolioDistributionId: string
}>()

const { slug } = useRoute().params as { slug: string }

const { n } = useExtendedI18n()
const isLoading = ref(false)
const modal = useModal()
const store = usePortfolioDistributionStore()
const portfolioDistribution = ref<DisbursementAllocation>({} as DisbursementAllocation)

const fetchAndOpenModal = async () => {
  isLoading.value = true
  modal.open()
  portfolioDistribution.value = await store.fetchPortfolioDistribution(props.portfolioDistributionId, { slug })
  isLoading.value = false
}

const getPortfolioDistributionType = (portfolioDistribution: DisbursementAllocation) => {
  return portfolioDistribution.term_sheet?.investment_type || portfolioDistribution.related_to
}

const termSheetDetails = computed(() => {
  const details = []

  if (getPortfolioDistributionType(portfolioDistribution.value) === 'Equity') {
    details.push(
      { label: 'Share Price', value: n(portfolioDistribution.value.share_price, 'currency') },
      { label: 'Shares Sold', value: format(portfolioDistribution.value.shares_sold, 'number') },
      { label: 'Total Amount', value: n(portfolioDistribution.value.total_received, 'currency') },
    )
  }

  if (['Convertible Note', 'Safe Note', 'Loan'].includes(getPortfolioDistributionType(portfolioDistribution.value))) {
    details.push(
      { label: 'Principal', value: n(portfolioDistribution.value.capital, 'currency') },
      { label: 'Interest', value: n(portfolioDistribution.value.interest_payment, 'currency') },
      { label: 'Total Amount', value: n(portfolioDistribution.value.total_received, 'currency') },
    )
  }

  if (['Revenue Share', 'Revenue'].includes(getPortfolioDistributionType(portfolioDistribution.value))) {
    details.push({ label: 'Payment', value: n(portfolioDistribution.value.total_received, 'currency') })
  }

  return details
})
</script>

<template>
  <template v-if="$slots.default">
    <slot :fetchAndOpenModal="fetchAndOpenModal" />
  </template>
  <VModal :modalStore="modal" :has_footer="false">
    <template #main>
      <VLoadingSection
        v-if="isLoading"
        class="mt-40"
        title="Preparing Your Form..."
        description="Please wait a moment while we set things up for you. Your data is being processed to ensure everything runs smoothly."
      />
      <div class="flex flex-col gap-6" v-else>
        <div class="flex items-center justify-between">
          <h2 class="text-xl font-medium">Portfolio Distribution</h2>
        </div>

        <div>
          <DescriptionList
            :items="[
              { label: 'Date', value: format(portfolioDistribution.date, 'date') },
              { label: 'Type', value: portfolioDistribution.type },
              ...(portfolioDistribution.company
                ? [{ label: 'Company', value: portfolioDistribution.company.name || '--', key: 'company' }]
                : []),
              { label: 'Related', value: portfolioDistribution.related_to || '--' },
            ]"
            :loading="isLoading"
            title="Distribution Details"
          >
            <template #company>
              <a
                v-if="portfolioDistribution.company"
                :href="`${rails_url()}/companies/${portfolioDistribution.company.id}`"
                class="hyperlink"
              >
                {{ portfolioDistribution.company.name }}
              </a>
              <span v-else>--</span>
            </template>
          </DescriptionList>
        </div>

        <div>
          <DescriptionList
            :items="termSheetDetails"
            :loading="isLoading"
            :title="portfolioDistribution.term_sheet.name"
          />
        </div>
      </div>
    </template>
  </VModal>
</template>
